import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../theme/tokens';
import { WriteButton } from '../../../components/ui';

/**
 * Side drawer wrapping `schema_json` editing for the two JSON-schema
 * catalogs (component_type, onboarding_component). Renders a Monaco
 * editor when `@monaco-editor/react` is available at runtime and
 * transparently falls back to a live-validated `<textarea>` when the
 * dep isn't installed.
 *
 * The Monaco import is lazy + error-boundary wrapped so the chunk only
 * loads (and only fails) the first time the drawer opens. See
 * `src/types/monacoEditorReact.d.ts` for the ambient declaration that
 * keeps `tsc --noEmit` happy before the real dep lands.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Row code being edited — displayed in the header. */
  code: string;
  /** Human-readable row name — displayed as the drawer title. */
  name: string;
  /** Current schema_json (null/undefined → empty editor). */
  value: object | null | undefined;
  /** Saving is driven by the parent mutation; this is purely the UI side. */
  saving: boolean;
  /** Called with the parsed JSON object; parent runs the mutation. */
  onSave: (next: object | null) => void;
}

/* ------------------------------------------------------------------ */
/*  Lazy-loaded Monaco — falls back to a <textarea> if not installed   */
/* ------------------------------------------------------------------ */

const LazyMonaco = React.lazy(async () => {
  try {
    const mod = await import('@monaco-editor/react');
    return { default: mod.default };
  } catch {
    // Render something that signals "not available" so the error
    // boundary swaps in the textarea fallback.
    throw new Error('monaco-not-installed');
  }
});

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  onError?: () => void;
}

class MonacoBoundary extends React.Component<ErrorBoundaryProps, { failed: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  componentDidCatch(): void {
    this.props.onError?.();
  }
  render(): React.ReactNode {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function parseSchemaText(text: string): { value: object | null; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { value: null, error: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: null, error: 'Schema must be a JSON object.' };
    }
    return { value: parsed, error: null };
  } catch (e: any) {
    return { value: null, error: `Invalid JSON: ${e?.message ?? 'parse error'}` };
  }
}

/* ------------------------------------------------------------------ */
/*  Drawer                                                             */
/* ------------------------------------------------------------------ */

const SchemaEditorDrawer: React.FC<Props> = ({
  open,
  onClose,
  code,
  name,
  value,
  saving,
  onSave,
}) => {
  const initialText = useMemo(
    () => (value ? JSON.stringify(value, null, 2) : ''),
    [value],
  );

  const [text, setText] = useState<string>(initialText);
  const [monacoFailed, setMonacoFailed] = useState<boolean>(false);

  // Reset the buffer each time the drawer (re)opens for a different row.
  useEffect(() => {
    if (open) setText(initialText);
  }, [open, initialText]);

  const validation = useMemo(() => parseSchemaText(text), [text]);

  const isDirty = text !== initialText;

  const handleSave = () => {
    if (validation.error) return;
    onSave(validation.value);
  };

  // Keyboard: ⌘S to save, Esc to close — matches the edit pages.
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      const cmd = ev.metaKey || ev.ctrlKey;
      if (cmd && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (isDirty && !validation.error && !saving) handleSave();
      } else if (ev.key === 'Escape' && !saving) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDirty, validation.error, saving]);

  /* ------------------ Textarea fallback ------------------ */

  const textareaFallback = (
    <TextField
      multiline
      fullWidth
      minRows={24}
      maxRows={40}
      value={text}
      onChange={(e) => setText(e.target.value)}
      disabled={saving}
      InputProps={{
        sx: {
          fontFamily: 'Fira Code, Consolas, monospace',
          fontSize: 12,
          alignItems: 'flex-start',
          backgroundColor: colors.bgCode,
        },
      }}
      error={!!validation.error}
      helperText={
        validation.error ??
        (text.trim()
          ? 'Valid JSON object.'
          : 'Leave blank to clear the schema for this row.')
      }
    />
  );

  /* ------------------ Monaco editor ------------------ */

  const monacoEditor = (
    <MonacoBoundary
      fallback={textareaFallback}
      onError={() => setMonacoFailed(true)}
    >
      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: colors.adminAccent }} />
          </Box>
        }
      >
        <Box
          sx={{
            border: `1px solid ${validation.error ? colors.errorBorder : colors.borderLight}`,
            borderRadius: '8px',
            overflow: 'hidden',
            height: 520,
          }}
        >
          <LazyMonaco
            value={text}
            language="json"
            theme="vs-light"
            height="520px"
            options={{
              minimap: { enabled: false },
              fontSize: 12,
              fontFamily: 'Fira Code, Consolas, monospace',
              lineNumbers: 'on',
              folding: true,
              automaticLayout: true,
              scrollBeyondLastLine: false,
              tabSize: 2,
              readOnly: saving,
            }}
            onChange={(v) => setText(v ?? '')}
          />
        </Box>
        <Typography
          sx={{
            fontSize: 12,
            color: validation.error ? colors.errorText : colors.textTertiary,
            mt: 1,
          }}
        >
          {validation.error ??
            (text.trim()
              ? 'Valid JSON object.'
              : 'Leave blank to clear the schema for this row.')}
        </Typography>
      </Suspense>
    </MonacoBoundary>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !saving && onClose()}
      PaperProps={{ sx: { width: { xs: '100%', sm: 720 } } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${colors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.bgSubtle,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
            schema_json editor
          </Typography>
          <Typography sx={{ ...typo.sectionTitle, fontSize: 16 }} noWrap>
            {name || code}
          </Typography>
          <Box
            component="code"
            sx={{
              ...typo.mono,
              fontSize: 11,
              color: colors.textSecondary,
              backgroundColor: colors.bgMuted,
              px: 0.5,
              py: 0.25,
              borderRadius: '4px',
              mt: 0.5,
              display: 'inline-block',
            }}
          >
            {code}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" disabled={saving}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        {monacoFailed && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ mb: 2, borderRadius: '8px' }}
          >
            Monaco editor isn&apos;t installed yet — using a validated textarea fallback. The
            experience is identical once <code>@monaco-editor/react</code> is installed.
          </Alert>
        )}
        {monacoEditor}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${colors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: colors.bgWhite,
        }}
      >
        <Tooltip title="Close without saving">
          <span>
            <Button onClick={onClose} sx={secondaryButtonSx} disabled={saving}>
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {isDirty && (
          <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
            Unsaved changes
          </Typography>
        )}
        <WriteButton
          variant="contained"
          onClick={handleSave}
          disabled={!isDirty || !!validation.error || saving}
          sx={{
            ...primaryButtonSx,
            backgroundColor: colors.adminAccent,
            color: colors.bgWhite,
            '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
          }}
        >
          {saving ? 'Saving…' : 'Save schema'}{' '}
          <Box component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5 }}>
            (⌘S)
          </Box>
        </WriteButton>
      </Box>
    </Drawer>
  );
};

export default SchemaEditorDrawer;
